import { Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import type { LookupResult, LookupRow } from './api';
import { dmy, fmtNum, fmtPct, monthText } from './format';
import styles from './style.module.scss';

export type GroupBy = 'email' | 'group' | 'none';

type GroupNode = {
  key: string;
  isGroup: true;
  label: string;
  sub: string;
  diamonds: number;
  compareDiamonds: number | null;
  bySegment: Record<string, number>;
  liveSeconds: number;
  liveDays: number;
  pkDiamonds: number;
  children: Node[];
};
type Node = (LookupRow & { key: string; isGroup?: false }) | GroupNode;

const isGroup = (n: Node): n is GroupNode => (n as GroupNode).isGroup === true;

/** Đường mini KC theo ngày; ngày không có số tô cam nhạt. */
function Spark({ daily, days }: { daily: Record<string, number>; days: string[] }) {
  if (days.length < 2) return null;
  const w = 84;
  const h = 22;
  const v = days.map((d) => daily[d] || 0);
  const max = Math.max(1, ...v);
  const x = (i: number) => (i * w) / (days.length - 1);
  const y = (n: number) => h - 2 - (n / max) * (h - 4);
  const d = v.map((n, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(n).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden className={styles.spark}>
      <path d={`${d} L${w} ${h} L0 ${h}Z`} className={styles.sparkArea} />
      <path d={d} className={styles.sparkLine} />
      <circle cx={x(v.length - 1)} cy={y(v[v.length - 1])} r={2} className={styles.sparkDot} />
    </svg>
  );
}

function Delta({ value }: { value: number | null }) {
  if (value === null || !Number.isFinite(value)) return <span className={styles.muted}>—</span>;
  return (
    <span className={value >= 0 ? styles.up : styles.down}>
      {value >= 0 ? '▲' : '▼'} {fmtPct(Math.abs(value))}
    </span>
  );
}

function RowTags({ r }: { r: LookupRow }) {
  return (
    <>
      {r.renamedFrom && (
        <Tooltip title={`Đã dán @${r.renamedFrom} — TikTok ID cũ của người này`}>
          <Tag color="cyan" className={styles.tag}>
            đổi ID
          </Tag>
        </Tooltip>
      )}
      {r.emailChanged && (
        <Tooltip title={`Có số dưới nhiều email trong kỳ: ${r.emails.join(', ')}`}>
          <Tag color="orange" className={styles.tag}>
            đổi Đại diện
          </Tag>
        </Tooltip>
      )}
      {r.departedAt && (
        <Tag color="red" className={styles.tag}>
          rời đại lý {dmy(r.departedAt)}
        </Tag>
      )}
      {!r.fromList && (
        <Tooltip title="Hệ thống tự thêm vì Creator thuộc Nhóm / email Đại diện đã chọn">
          <Tag className={styles.tag} bordered={false}>
            hệ thống
          </Tag>
        </Tooltip>
      )}
    </>
  );
}

export default function ResultTable({
  result,
  rows,
  groupBy,
}: {
  result: LookupResult;
  rows: LookupRow[];
  groupBy: GroupBy;
}) {
  const multi = result.segments.length > 1;
  const hasCompare = !!result.compare?.available;
  const total = result.totals.diamonds || 1;
  const maxKc = Math.max(1, ...rows.map((r) => r.diamonds));
  // Ngày của đoạn duy nhất (để vẽ đường mini) — chỉ khi có file ngày.
  const days = useMemo(() => {
    if (multi || !result.daily.length) return [] as string[];
    const s = result.segments[0];
    const out: string[] = [];
    const last = result.daily[result.daily.length - 1].date;
    for (let d = s.start; d <= last && d <= s.end; ) {
      out.push(d);
      d = new Date(Date.parse(`${d}T00:00:00Z`) + 86400000).toISOString().slice(0, 10);
    }
    return out;
  }, [multi, result]);
  const heatMax = useMemo(
    () => Math.max(1, ...rows.flatMap((r) => result.segments.map((s) => r.bySegment[s.key] || 0))),
    [rows, result.segments]
  );

  const data: Node[] = useMemo(() => {
    const leaves: Node[] = rows.map((r) => ({ ...r, key: r.uid }));
    if (groupBy === 'none') return leaves;
    const map = new Map<string, GroupNode>();
    for (const r of rows) {
      const label = (groupBy === 'email' ? r.email : r.groupName) || '(chưa có trong kỳ)';
      let g = map.get(label);
      if (!g) {
        g = {
          key: `g:${label}`,
          isGroup: true,
          label,
          sub: '',
          diamonds: 0,
          compareDiamonds: hasCompare ? 0 : null,
          bySegment: {},
          liveSeconds: 0,
          liveDays: 0,
          pkDiamonds: 0,
          children: [],
        };
        map.set(label, g);
      }
      g.children.push({ ...r, key: r.uid });
      g.diamonds += r.diamonds;
      if (g.compareDiamonds !== null) g.compareDiamonds += r.compareDiamonds || 0;
      g.liveSeconds += r.liveSeconds;
      g.liveDays += r.liveDays;
      g.pkDiamonds += r.pkDiamonds;
      for (const s of result.segments) g.bySegment[s.key] = (g.bySegment[s.key] || 0) + (r.bySegment[s.key] || 0);
      if (groupBy === 'email' && r.groupName && !g.sub) g.sub = r.groupName;
    }
    return Array.from(map.values()).sort((a, b) => b.diamonds - a.diamonds);
  }, [rows, groupBy, hasCompare, result.segments]);

  const [collapsed, setCollapsed] = useState<string[]>([]);
  const groupKeys = useMemo(() => data.filter(isGroup).map((g) => g.key), [data]);
  const expandedRowKeys = groupKeys.filter((k) => !collapsed.includes(k));

  const kcSorter = (a: Node, b: Node) => a.diamonds - b.diamonds;
  const columns: ColumnsType<Node> = [
    {
      title: 'Creator',
      key: 'who',
      fixed: 'left',
      width: 250,
      sorter: (a, b) =>
        (isGroup(a) ? a.label : a.username || '').localeCompare(isGroup(b) ? b.label : b.username || ''),
      render: (_, n) =>
        isGroup(n) ? (
          <div className={styles.groupCell}>
            <b>{n.label}</b>
            <span>
              {n.children.length} Creator{n.sub ? ` · ${n.sub}` : ''}
            </span>
          </div>
        ) : (
          <div className={styles.who}>
            <b>
              {n.username ? `@${n.username}` : '(chưa có TikTok ID)'}
              <RowTags r={n} />
            </b>
            <Typography.Text copyable={{ text: n.uid, tooltips: ['Sao chép UID', 'Đã sao chép'] }} className={styles.uid}>
              {n.uid}
            </Typography.Text>
          </div>
        ),
    },
    ...(groupBy === 'email'
      ? []
      : [
          {
            title: 'Đại diện',
            key: 'email',
            width: 220,
            render: (_: unknown, n: Node) =>
              isGroup(n) ? null : (
                <div className={styles.emailCell}>
                  <span>{n.email || '—'}</span>
                  {n.groupName && <small>{n.groupName}</small>}
                </div>
              ),
          },
        ]),
  ];

  if (multi) {
    for (const s of result.segments) {
      columns.push({
        title: monthText(s.key),
        key: s.key,
        align: 'right',
        width: 120,
        sorter: (a, b) => (a.bySegment[s.key] || 0) - (b.bySegment[s.key] || 0),
        render: (_, n) => {
          const v = n.bySegment[s.key] || 0;
          if (isGroup(n)) return <b>{fmtNum(v)}</b>;
          const pct = Math.round(8 + 62 * Math.sqrt(v / heatMax));
          return (
            <span className={styles.heat} style={{ background: `rgba(194, 51, 125, ${pct / 100})`, color: pct > 45 ? '#fff' : undefined }}>
              {fmtNum(v)}
            </span>
          );
        },
      });
    }
    columns.push(
      {
        title: 'Tổng',
        key: 'kc',
        align: 'right',
        width: 130,
        defaultSortOrder: 'descend',
        sorter: kcSorter,
        render: (_, n) => <b>{fmtNum(n.diamonds)}</b>,
      },
      {
        title: 'Xu hướng',
        key: 'trend',
        width: 100,
        render: (_, n) => {
          const vals = result.segments.map((s) => n.bySegment[s.key] || 0);
          const first = vals.find((v) => v > 0) || 0;
          const last = vals[vals.length - 1];
          return first ? <Delta value={(last - first) / first} /> : <span className={styles.muted}>—</span>;
        },
      }
    );
  } else {
    columns.push(
      {
        title: 'Kim cương',
        key: 'kc',
        align: 'right',
        width: 160,
        defaultSortOrder: 'descend',
        sorter: kcSorter,
        render: (_, n) => (
          <span className={styles.kcCell}>
            {!isGroup(n) && <i className={styles.kcBar} style={{ width: Math.max(2, (44 * n.diamonds) / maxKc) }} />}
            <b>{fmtNum(n.diamonds)}</b>
          </span>
        ),
      },
      ...(hasCompare
        ? [
            {
              title: 'So kỳ trước',
              key: 'delta',
              align: 'right' as const,
              width: 120,
              sorter: (a: Node, b: Node) => deltaOf(a) - deltaOf(b),
              render: (_: unknown, n: Node) => (
                <Tooltip title={`Kỳ so sánh: ${fmtNum(n.compareDiamonds || 0)}`}>
                  <span>
                    <Delta value={n.compareDiamonds ? (n.diamonds - n.compareDiamonds) / n.compareDiamonds : null} />
                  </span>
                </Tooltip>
              ),
            },
          ]
        : []),
      {
        title: '% tập',
        key: 'share',
        align: 'right',
        width: 80,
        render: (_, n) => <span className={styles.muted}>{fmtPct(n.diamonds / total)}</span>,
      },
      {
        title: 'Giờ LIVE',
        key: 'hours',
        align: 'right',
        width: 90,
        sorter: (a, b) => a.liveSeconds - b.liveSeconds,
        render: (_, n) => (n.liveSeconds / 3600).toLocaleString('vi-VN', { maximumFractionDigits: 1 }),
      },
      {
        title: 'Ngày hợp lệ',
        key: 'days',
        align: 'right',
        width: 100,
        sorter: (a, b) => a.liveDays - b.liveDays,
        render: (_, n) => fmtNum(n.liveDays),
      },
      {
        title: 'KC PK',
        key: 'pk',
        align: 'right',
        width: 120,
        sorter: (a, b) => a.pkDiamonds - b.pkDiamonds,
        render: (_, n) => fmtNum(n.pkDiamonds),
      }
    );
    if (days.length > 1)
      columns.push({
        title: 'Theo ngày',
        key: 'spark',
        width: 100,
        render: (_, n) => (isGroup(n) ? null : <Spark daily={n.daily} days={days} />),
      });
  }

  const sumSeg = (key: string) => rows.reduce((a, r) => a + (r.bySegment[key] || 0), 0);
  const sumKc = rows.reduce((a, r) => a + r.diamonds, 0);

  return (
    <Table<Node>
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={data}
      rowClassName={(n) => (isGroup(n) ? styles.groupRow : '')}
      expandable={{
        expandedRowKeys,
        onExpand: (open, n) =>
          setCollapsed((c) => (open ? c.filter((k) => k !== n.key) : [...c, n.key])),
      }}
      pagination={{ defaultPageSize: groupBy === 'none' ? 100 : 30, showSizeChanger: true, hideOnSinglePage: true }}
      scroll={{ x: 'max-content', y: 620 }}
      summary={() => (
        <Table.Summary fixed="bottom">
          <Table.Summary.Row className={styles.sumRow}>
            <Table.Summary.Cell index={0}>
              <b>Tổng · {rows.length} Creator</b>
            </Table.Summary.Cell>
            {groupBy !== 'email' && <Table.Summary.Cell index={1} />}
            {multi ? (
              <>
                {result.segments.map((s, i) => (
                  <Table.Summary.Cell key={s.key} index={2 + i} align="right">
                    <b>{fmtNum(sumSeg(s.key))}</b>
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={99} align="right">
                  <b>{fmtNum(sumKc)}</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={100} />
              </>
            ) : (
              <>
                <Table.Summary.Cell index={2} align="right">
                  <b>{fmtNum(sumKc)}</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} colSpan={columns.length - (groupBy !== 'email' ? 3 : 2)} />
              </>
            )}
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
}

function deltaOf(n: Node): number {
  return n.compareDiamonds ? (n.diamonds - n.compareDiamonds) / n.compareDiamonds : -Infinity;
}
